export type HelmUpgradeCommandValue = {
    key: string;
    value: string | string[];
    useSingleQuotes?: boolean;
    useSetJson?: boolean;
};

export class HelmUpgradeCommandBuilder {
    private readonly newLine: string;
    private readonly headTokens: string[];
    private readonly tailTokens: string[];
    private readonly flags: string[] = [];
    private readonly values: HelmUpgradeCommandValue[] = [];

    constructor(version: string, namespace: string, releaseName: string, registryUrl: string, scriptLineBreakChar: string, install: boolean = true, createNamespace: boolean = true) {
        this.newLine = `${scriptLineBreakChar}\n`;

        let mainCommandToken = "helm upgrade";
        if (install) {
            mainCommandToken = `${mainCommandToken} --install`;
        }
        mainCommandToken = `${mainCommandToken} --atomic`;

        let namespaceToken = `--namespace ${namespace}`;
        if (createNamespace) {
            namespaceToken = `--create-namespace ${namespaceToken}`;
        }

        this.headTokens = [mainCommandToken, namespaceToken, `--version "${version}"`];
        this.tailTokens = [releaseName, registryUrl];
    }

    public withFlag(flag: string) {
        this.flags.push(flag.trim());
        return this;
    }

    public withValue(key: string, value: string | string[] | undefined) {
        const trimmedKey = key.trim();

        const normalizedValue = this.normalizeValue(value);

        if (trimmedKey !== "" && normalizedValue !== "") {
            this.values.push({ key: trimmedKey, value: normalizedValue });
        }

        return this;
    }

    public withValues(setValues: HelmUpgradeCommandValue[]) {
        this.values.push(...setValues.map((v) => ({ key: v.key.trim(), value: this.normalizeValue(v.value), useSingleQuotes: v.useSingleQuotes, useSetJson: v.useSetJson })).filter((v) => v.key !== "" && v.value !== ""));
        return this;
    }

    public toCommandString(): string {
        const allTokens = [...this.headTokens, ...this.flags, ...this.values.map(this.toSetString), ...this.tailTokens];
        return allTokens.join(` ${this.newLine}`).trim();
    }

    private toSetString(setValue: HelmUpgradeCommandValue): string {
        if (setValue.useSetJson) {
            return `--set-json '${setValue.key}=${setValue.value}'`;
        }
        const quote = setValue.useSingleQuotes ? "'" : '"';
        return `--set ${setValue.key}=${quote}${setValue.value}${quote}`;
    }

    private normalizeValue(value: string | string[] | undefined): string {
        if (Array.isArray(value)) {
            return `{${value
                .map((s) => s.trim())
                .filter((s) => s !== "")
                .join(",")}}`;
        }

        return (value || "").trim();
    }
}
